import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

export type Locale = 'zh-CN' | 'en-US'

const zh = {
  'locale.label': '界面语言',
  'scenario.intro': '介绍',
  'scenario.intro.detail': '架构、能力与发布边界',
  'scenario.gallery': '组件展厅',
  'scenario.gallery.detail': '生产用例与核心能力',
  'scenario.analysis': '经营分析',
  'scenario.analysis.detail': '业务指标与复合单元格',
  'scenario.spreadsheet': '电子表格',
  'scenario.spreadsheet.detail': 'Excel 风格编辑与单元格配置',
  'analysis.eyebrow': '经营驾驶舱 · 2026 Q2',
  'analysis.title': '全渠道经营健康分析',
  'analysis.live': '数据已更新',
  'analysis.period': '本季度',
  'analysis.view.flat': '区域视图',
  'analysis.view.tree': '组合视图',
  'analysis.kpi.revenue': '累计收入',
  'analysis.kpi.revenue.note': '较上期增加 ¥4.46M',
  'analysis.kpi.attainment': '目标达成率',
  'analysis.kpi.attainment.note': '6 个区域中 4 个达标',
  'analysis.kpi.margin': '综合毛利率',
  'analysis.kpi.margin.note': '高毛利产品占比 58%',
  'analysis.kpi.risk': '风险业务单元',
  'analysis.kpi.risk.note': '其中 3 项需本周处理',
  'analysis.narrative.label': 'AI 经营洞察',
  'analysis.narrative.text': '华东收入保持领先，Nova BI 拉动毛利率改善；海外渠道达成率偏低，建议重点跟进伙伴交付与回款节奏。',
  'analysis.detail.title': '经营单元明细',
  'analysis.detail.subtitle': '区域 × 产品 × 指标 · 支持下钻与复合渲染',
  'analysis.legend.label': '单元格渲染能力',
  'analysis.legend.component': '复合组件',
  'analysis.legend.dataBar': '数据条',
  'analysis.legend.colorScale': '连续色阶',
  'analysis.legend.rules': '图标 / 条件文本',
  'analysis.section.label': '经营分析列组',
  'analysis.section.core': '核心经营',
  'analysis.section.signals': '条件预警',
  'analysis.cell.productMix': '收入贡献 {value}%',
  'analysis.cell.orders': '新客 {value}',
  'spreadsheet.bookLabel': '销售计划.xlsx',
  'spreadsheet.toolbar.tabs': '电子表格菜单',
  'spreadsheet.toolbar.label': '单元格格式工具栏',
  'spreadsheet.tab.home': '开始',
  'spreadsheet.tab.formulas': '公式',
  'spreadsheet.tab.view': '视图',
  'spreadsheet.saved': '更改已应用',
  'spreadsheet.sessionReady': '演示数据 · 本标签页暂存',
  'spreadsheet.unsaved': '未保存到文件 · 已在本标签页暂存',
  'spreadsheet.group.history': '历史记录',
  'spreadsheet.group.clipboard': '剪贴板',
  'spreadsheet.group.font': '字体',
  'spreadsheet.group.alignment': '对齐方式',
  'spreadsheet.group.number': '数字格式',
  'spreadsheet.group.editing': '编辑',
  'spreadsheet.undo': '撤销',
  'spreadsheet.redo': '重做',
  'spreadsheet.copy': '复制',
  'spreadsheet.cut': '剪切',
  'spreadsheet.paste': '粘贴',
  'spreadsheet.font': '字体',
  'spreadsheet.fontSize': '字号',
  'spreadsheet.bold': '加粗',
  'spreadsheet.italic': '斜体',
  'spreadsheet.underline': '下划线',
  'spreadsheet.textColor': '字体颜色',
  'spreadsheet.fillColor': '填充颜色',
  'spreadsheet.color.apply': '应用上次使用的字体颜色 {color}',
  'spreadsheet.fill.apply': '应用上次使用的填充颜色 {color}',
  'spreadsheet.color.palette': '打开{target}色板',
  'spreadsheet.alignLeft': '左对齐',
  'spreadsheet.alignCenter': '居中对齐',
  'spreadsheet.alignRight': '右对齐',
  'spreadsheet.wrap': '自动换行',
  'spreadsheet.currency': '货币格式',
  'spreadsheet.percent': '百分比格式',
  'spreadsheet.number': '保留两位小数',
  'spreadsheet.numberFormat': '数字格式',
  'spreadsheet.numberFormat.general': '常规',
  'spreadsheet.numberFormat.number': '数字',
  'spreadsheet.numberFormat.currency': '货币',
  'spreadsheet.numberFormat.percent': '百分比',
  'spreadsheet.merge': '合并所选单元格',
  'spreadsheet.unmerge': '取消合并单元格',
  'spreadsheet.reset': '重置工作表',
  'spreadsheet.clearFormat': '清除所选单元格格式',
  'spreadsheet.nameBox': '名称框',
  'spreadsheet.nameBoxHint': '输入 C4 或 B2:D8 并回车定位',
  'spreadsheet.formulaBar': '公式栏',
  'spreadsheet.insertFunction': '插入函数',
  'spreadsheet.cellEditor': '编辑单元格',
  'spreadsheet.cancelEdit': '取消编辑',
  'spreadsheet.acceptEdit': '确认编辑',
  'spreadsheet.gridLabel': '销售计划电子表格',
  'spreadsheet.sheet.subtitle': '销售运营中心 · 人民币 · 数据更新于 07/14 18:30',
  'spreadsheet.sheetName': '经营跟踪',
  'spreadsheet.status.summary': '计数 {count} · 平均 {average} · 求和 {sum}',
  'spreadsheet.status.count': '计数 {count}',
  'spreadsheet.formula.quick': '快速函数',
  'spreadsheet.formula.sum': '求和',
  'spreadsheet.formula.average': '平均值',
  'spreadsheet.formula.min': '最小值',
  'spreadsheet.formula.max': '最大值',
  'spreadsheet.formula.count': '计数',
  'spreadsheet.formula.help': '选择数据后插入函数；可先检查或修改公式，再按 Enter 确认，按 Esc 取消。',
  'spreadsheet.view.gridlines': '网格线',
  'spreadsheet.view.freeze': '冻结标题行',
  'spreadsheet.view.formulaBar': '公式栏',
  'spreadsheet.view.zoom': '缩放',
  'spreadsheet.view.zoomIn': '放大',
  'spreadsheet.view.zoomOut': '缩小',
  'spreadsheet.feedback.formatted': '格式已应用',
  'spreadsheet.feedback.formatCleared': '格式已清除',
  'spreadsheet.feedback.cleared': '内容已清除',
  'spreadsheet.feedback.invalidAddress': '地址无效，请输入如 C4 或 B2:D8',
  'spreadsheet.feedback.copied': '已复制选区',
  'spreadsheet.feedback.copiedInternal': '已复制到表格剪贴板；浏览器未授予系统剪贴板权限',
  'spreadsheet.feedback.cut': '已选择待移动区域；粘贴后移动，按 Esc 取消',
  'spreadsheet.feedback.cutClipboardFailed': '剪切已取消：无法写入系统剪贴板，源数据未删除',
  'spreadsheet.feedback.cutChanged': '剪切已取消：工作表在粘贴前发生变化',
  'spreadsheet.feedback.cutCancelled': '已取消剪切，源数据保持不变',
  'spreadsheet.cutPendingStatus': '待移动 {range} · Esc 取消',
  'spreadsheet.feedback.pasted': '已粘贴',
  'spreadsheet.feedback.pastedCount': '已粘贴 {count} 个单元格',
  'spreadsheet.feedback.clipboardEmpty': '剪贴板为空',
  'spreadsheet.feedback.pasteTooLarge': '粘贴区域过大',
  'spreadsheet.feedback.pasteOutOfBounds': '无法从 {address} 粘贴 {rows} × {columns}：目标区域超出工作表边界',
  'spreadsheet.feedback.mergeConflict': '所选区域与已有合并单元格冲突',
  'spreadsheet.feedback.mergeRange': '请先选择两个或更多单元格',
  'spreadsheet.feedback.merged': '单元格已合并',
  'spreadsheet.feedback.unmerged': '已取消合并',
  'spreadsheet.feedback.formulaInserted': '函数公式已进入编辑；按 Enter 确认，按 Esc 取消',
  'spreadsheet.feedback.reset': '工作表已重置',
  'spreadsheet.feedback.undo': '已撤销',
  'spreadsheet.feedback.redo': '已重做',
  'spreadsheet.confirm.merge': '合并将清除左上角以外 {count} 个非空单元格。此操作可撤销，是否继续？',
  'spreadsheet.confirm.reset': '重置将替换当前工作表内容和格式。此操作可撤销，是否继续？',
  'gallery.eyebrow': '可运行示例',
  'gallery.title': '逐项验证公开能力',
  'gallery.intro': '先以真实业务场景验证组合能力，再逐项检查渲染底座；每次只挂载一个表格。',
  'gallery.group.production': '生产用例',
  'gallery.group.basic': '基础能力',
  'gallery.group.advanced': '进阶能力',
  'gallery.editor.open': '编辑源码',
  'gallery.editor.close': '收起源码',
  'gallery.editor.title': '组件实现源码',
  'gallery.editor.live': '实时渲染',
  'gallery.editor.reset': '重置源码',
  'gallery.editor.hint': '编辑 TSX 后自动刷新预览',
  'gallery.editor.preview': '实时预览',
  'intro.eyebrow': 'Studio · 应用层 · 渲染底座',
  'intro.title': 'Studio + 应用层表格 + 表格渲染底座',
  'intro.detail': 'Studio 提供体验与配置，Insight 组织业务表格能力，Core 承担高性能可视 DOM 渲染。三层边界清晰，按需组合。',
  'intro.stats.scale': '逻辑单元格',
  'intro.stats.examples': '可运行示例',
  'intro.stats.packages': '架构层',
  'intro.layers.title': '仓库与发布边界',
  'intro.layer.studio': '交互演示、实时 Props、源码入口与性能诊断。',
  'intro.layer.studio.meta': 'Studio 交互层',
  'intro.layer.insight': '树形、条件格式、业务单元格与导出能力。',
  'intro.layer.insight.meta': '应用层表格',
  'intro.layer.core': '虚拟化、固定、合并、选择与键盘交互引擎。',
  'intro.layer.core.meta': '表格渲染底座',
  'intro.capabilities.title': '能力覆盖',
  'intro.capabilities.basic': '双轴虚拟化 · 四向固定 · 自定义与自适应尺寸 · 点击、拖选、越界滚动与 Shift 扩选 · 方向键/Tab/Enter · TSV 复制 · 主题与外观 · 空态、事件、meta、ARIA 与 i18n',
  'intro.capabilities.advanced': '10 万 × 10 万惰性数据 · 显式/同值/树边界合并 · 平铺与任意深度异步树 · 五类条件格式与规则优先级 · 排版/图片/图标/React 组件 · 移动端触控选区、列宽拖拽与复制 · 命令式 API · Excel/CSV/PNG',
  'gallery.virtualization.title': '10 万 × 10 万虚拟化',
  'gallery.virtualization.detail': '百亿级逻辑坐标只渲染可视窗口及少量缓冲 DOM。',
  'gallery.frozen.title': '四向固定',
  'gallery.frozen.detail': '顶部、底部、左侧与右侧共享同一滚动坐标系。',
  'gallery.sizing.title': '行列尺寸与容器适配',
  'gallery.sizing.detail': '稀疏尺寸 Map、内容测量与少列拉伸可以组合使用。',
  'gallery.sizing.dimension': '维度 {index}',
  'gallery.sizing.measured': '由可见内容测量',
  'gallery.sizing.cell': '第 {row} 行 · 第 {column} 列',
  'gallery.merging.title': '相邻维度自动合并',
  'gallery.merging.detail': '相邻同值维度自动纵向合并，并与显式横向合并共存。',
  'gallery.merging.region': '区域',
  'gallery.merging.product': '产品',
  'gallery.merging.revenue': '收入',
  'gallery.merging.orders': '订单',
  'gallery.merging.margin': '利润率',
  'gallery.merging.summary': '首行横向汇总',
  'gallery.merging.east': '华东',
  'gallery.merging.south': '华南',
  'gallery.merging.north': '华北',
  'gallery.merging.cloud': '云服务',
  'gallery.merging.data': '数据',
  'gallery.merging.ai': 'AI',
  'gallery.selection.title': '选区与复制',
  'gallery.selection.detail': '点击、方向键、拖拽圈选和 TSV 复制共用受控选区。',
  'gallery.selection.toolbar': '受控选区',
  'gallery.selection.tsv': '复制 TSV',
  'gallery.selection.rows': '已复制 {count} 行',
  'gallery.selection.value': '值 {row}.{column}',
  'gallery.renderer.title': '单元格自定义渲染',
  'gallery.renderer.detail': '图片、排版、条件格式与内嵌 React 组件保持清晰 DOM 分层。',
  'gallery.renderer.owner': '负责人',
  'gallery.renderer.team': '团队',
  'gallery.renderer.score': '评分',
  'gallery.renderer.status': '状态',
  'gallery.renderer.team.growth': '增长',
  'gallery.renderer.team.platform': '平台',
  'gallery.renderer.team.commerce': '商业化',
  'gallery.renderer.team.data': '数据',
  'gallery.renderer.team.ai': 'AI',
  'gallery.renderer.team.cloud': '云服务',
  'gallery.renderer.status.healthy': '健康',
  'gallery.renderer.status.watch': '关注',
  'gallery.renderer.status.risk': '风险',
  'gallery.tree.title': '多级树形行模型',
  'gallery.tree.detail': '根节点和中间分支都可独立展开，叶节点保持稳定数据坐标。',
  'gallery.tree.commercial': '商业',
  'gallery.tree.direct': '直营',
  'gallery.tree.partners': '伙伴',
  'gallery.tree.product': '产品',
  'gallery.tree.cloud': '云服务',
  'gallery.tree.data': '数据',
  'gallery.tree.enterprise': '企业客户',
  'gallery.tree.midmarket': '中型客户',
  'gallery.tree.platform': '平台',
  'gallery.tree.applications': '应用',
  'gallery.tree.analytics': '分析',
  'gallery.tree.governance': '治理',
  'gallery.tree.dimension': '维度',
  'gallery.tree.value': '数值',
  'gallery.conditional.title': '五类条件格式',
  'gallery.conditional.detail': '文本、背景、图标、色阶和数据条在同一表格中独立组合。',
  'gallery.conditional.entity': '业务单元',
  'gallery.conditional.unit': '单元 {index}',
  'gallery.conditional.bar': '数据条',
  'gallery.conditional.scale': '色阶',
  'gallery.conditional.icon': '图标',
  'gallery.conditional.rule': '文本与背景',
  'gallery.lazy.title': '惰性行列数据',
  'gallery.lazy.detail': 'rowSource 与 getColumn 按坐标生成 10 万行 × 1 万列，不物化完整数组。',
  'gallery.lazy.column': '指标 {index}',
  'gallery.api.title': '命令式 API',
  'gallery.api.detail': '通过 apiRef 定位单元格、控制选区并恢复表格焦点。',
  'gallery.api.toolbar': 'API 控制',
  'gallery.api.jump': '跳到 24,000 × 240',
  'gallery.api.select': '选中远端区域',
  'gallery.orders.title': '订单履约中心',
  'gallery.orders.detail': '万级订单窗口组合固定列、SLA 风险、金额日期、长文本与可复制选区。',
  'gallery.budget.title': '年度预算矩阵',
  'gallery.budget.detail': '成本中心横跨全年预算、实际与差异列，验证超宽滚动、固定维度与正负信号。',
  'gallery.mobile.title': '移动现场巡检',
  'gallery.mobile.detail': '现场工单在真实移动视口中组合主轴锁定、轻点选中、手柄扩选、列宽调整与安全区复制。',
  'gallery.mobile.state.selection': '当前选区',
  'gallery.mobile.state.resize': '最近调整',
  'gallery.mobile.state.title': '实时状态',
  'gallery.mobile.deviceLabel': '移动设备预览',
  'gallery.mobile.referenceSize': '桌面参考视口',
  'gallery.mobile.deviceTitle': '现场巡检工单',
  'gallery.mobile.deviceMeta': '{count} 个待巡检工单',
  'gallery.mobile.deviceLive': '实时',
  'gallery.mobile.copy': '复制选区',
  'gallery.mobile.copied': '已复制',
  'gallery.mobile.copyError': '复制失败',
  'gallery.mobile.selectionHandle': '拖动以扩展选区',
  'gallery.mobile.selectionActions': '选区操作',
  'gallery.mobile.range': 'R{rowStart}–{rowEnd} · C{columnStart}–{columnEnd}',
  'gallery.mobile.rangeEmpty': '尚未选择',
  'gallery.mobile.resizeHandle': '调整第 {column} 列宽度',
  'gallery.mobile.resizeReady': '可拖动表头边界',
  'gallery.mobile.resized': 'C{column} · {width}px',
  'gallery.mobile.column.item': '工单',
  'gallery.mobile.column.owner': '工程师',
  'gallery.mobile.column.progress': 'SLA',
  'gallery.mobile.column.status': '状态',
  'gallery.mobile.column.metric': '巡检项 {index}',
  'gallery.mobile.item': 'WO-{index}',
  'gallery.mobile.owner': '工程师 {index}',
  'gallery.mobile.status.ready': '已完成',
  'gallery.mobile.status.active': '处理中',
  'gallery.mobile.status.watch': '超时风险',
  'table.copySelection': '复制',
  'table.copySuccess': '已复制',
  'table.copyError': '复制失败',
  'table.selectionHandle': '拖动以扩展选区',
  'table.selectionActions': '选区操作',
  'table.resizeColumn': '调整列宽：{column}',
  'gallery.export.title': 'Excel、CSV 与图片',
  'gallery.export.detail': '同一表格 API 导出结构化工作簿、原始数据或当前可见视口。',
  'gallery.export.unit': '业务单元 {index}',
  'gallery.export.excel': '导出 Excel',
  'gallery.export.csv': '导出 CSV',
  'gallery.export.png': '导出 PNG',
  'preset.everyday': '日常数据',
  'preset.wide': '超宽报表',
  'preset.maximum': '极限规模',
  'preset.custom': '自定义规模',
  'studio.scenarios': '扩展场景',
  'studio.empty.title': '预览舞台已就绪',
  'studio.empty.body': '通过 renderStage 注入 UltiGrid 组件，参数会以 context 传入。',
  'studio.sampling': '采样中',
  'studio.diagnostics': '渲染诊断',
  'studio.diagnostics.source': '来自浏览器动画帧与 viewport 回调',
  'studio.diagnostics.note': '指标用于当前会话观测，不代表跨设备 benchmark 结论。',
  'studio.diagnostics.close': '关闭渲染诊断',
  'studio.diagnostics.raf': 'rAF',
  'studio.diagnostics.p95': 'P95',
  'studio.diagnostics.cells': 'DOM',
  'studio.diagnostics.visible': '可见区',
  'studio.diagnostics.warming': '预热中',
  'studio.diagnostics.idle': '空闲',
  'studio.diagnostics.scrolling': '滚动中',
  'studio.diagnostics.hidden': '页面隐藏',
  'studio.reset': '重置配置',
  'studio.export': '导出',
  'studio.export.preparing': '表格加载中，导出暂不可用',
  'studio.exporting': '正在导出 {format}',
  'studio.export.dialog': '选择导出格式',
  'studio.export.title': '导出视图',
  'studio.export.subtitle': '选择适合后续工作的格式',
  'studio.export.close': '关闭导出面板',
  'studio.export.sample': '当前逻辑表过大，Excel 与 CSV 将安全抽样前 2,000 行 × 128 列。',
  'studio.export.excel': 'Excel 工作簿',
  'studio.export.excel.detail': '.xlsx · 保留结构与合并',
  'studio.export.image': '当前视图图片',
  'studio.export.image.detail': '.png · 捕获可见 viewport',
  'studio.export.csv': '原始数据',
  'studio.export.csv.detail': '.csv · 纯数据或安全样本',
  'studio.inspector.toggle': '切换参数面板',
  'studio.inspector.collapse': '收起参数面板',
  'studio.inspector.expand': '展开参数面板',
  'studio.stage.label': 'UltiGrid Insight 预览舞台',
  'studio.scale': '数据规模',
  'studio.scale.preset': '数据规模预设',
  'studio.fullscreen.enter': '进入全屏',
  'studio.fullscreen.exit': '退出全屏',
  'studio.fullscreen.failed': '当前环境无法进入全屏',
  'studio.render.failed': '渲染未能完成',
  'studio.render.retry': '重新渲染',
  'studio.render.loading': '正在构建可视区域',
  'studio.render.loading.detail': '仅创建 viewport 内的 DOM 节点',
  'studio.status.pinnedRows': '{count} 个固定行',
  'studio.status.pinnedColumns': '{count} 个固定列',
  'studio.status.cells': '单元格 {count}',
  'studio.status.scroll': '{count} px/s',
  'studio.status.live': '实时',
  'studio.status.rendering': '渲染中',
  'studio.status.error': '错误',
  'studio.inspector.close': '关闭参数面板',
  'studio.inspector.label': 'Props 实时配置面板',
  'studio.inspector.title': 'Props Lab',
  'studio.inspector.resize': '拖动调整参数面板宽度',
  'studio.inspector.resize.hint': '拖动或使用左右方向键调整面板宽度',
  'studio.connected': '已连接',
  'studio.editor.mode': '编辑器模式',
  'studio.editor.visual': '可视化配置',
  'studio.editor.pending': '存在未应用修改',
  'studio.summary.grid': '逻辑网格',
  'studio.summary.scenario': '当前场景',
  'studio.summary.frozen': '固定区域',
  'studio.section.engine': '数据与引擎',
  'studio.section.engine.detail': '失焦或 Enter 后应用数值',
  'studio.field.rows': '行数',
  'studio.field.columns': '列数',
  'studio.field.overscanRows': '行缓冲',
  'studio.field.overscanColumns': '列缓冲',
  'studio.section.frozen': '固定区域',
  'studio.section.frozen.detail': '上下左右独立配置',
  'studio.field.top': '顶部行',
  'studio.field.bottom': '底部行',
  'studio.field.left': '左侧列',
  'studio.field.right': '右侧列',
  'studio.section.size': '尺寸与密度',
  'studio.section.size.detail': '控制几何和自适应行为',
  'studio.field.rowHeight': '默认行高',
  'studio.field.columnWidth': '默认列宽',
  'studio.field.density': '界面密度',
  'studio.density.compact': '紧凑',
  'studio.density.comfortable': '适中',
  'studio.density.relaxed': '宽松',
  'studio.field.fitColumns': '自适应列宽',
  'studio.field.fitColumns.hint': '少列时平分容器',
  'studio.field.autoRowHeight': '内容自适应行高',
  'studio.field.autoRowHeight.hint': '测量当前可见多行内容',
  'studio.section.presentation': '呈现细节',
  'studio.section.presentation.detail': '轻量视觉层',
  'studio.field.themeColor': '主题色',
  'studio.field.gridLines': '显示网格线',
  'studio.field.striped': '交替行底色',
  'studio.field.rowNumbers': '显示行号',
  'studio.field.treeEnabled': '树形展示',
  'studio.field.mergeSameValueDimensions': '合并相同维度',
  'studio.field.treeExpanded': '默认展开树节点',
  'studio.json.title': '先验证，再应用',
  'studio.json.body': '编辑只保存在草稿；确认无误后按 ⌘↵ 或点击应用，避免极限数据在输入过程中反复重建。',
  'studio.json.copy': '复制',
  'studio.json.copied': '已复制',
  'studio.json.copyTitle': '复制 JSON',
  'studio.json.discard': '放弃草稿',
  'studio.json.discardTitle': '放弃草稿',
  'studio.json.validPending': 'Schema valid · 等待应用',
  'studio.json.synced': '已与预览同步',
  'studio.json.restore': '恢复默认',
  'studio.json.apply': '应用 Props',
  'studio.shortcuts': '快捷键',
  'studio.shortcuts.panel': '面板',
  'studio.shortcuts.reset': '重置',
  'app.source.open': '查看源码',
  'error.propsObject': 'Props 必须是一个 JSON 对象',
  'error.scenario': 'scenario 仅支持 intro / gallery / analysis / spreadsheet',
  'error.density': 'density 仅支持 compact / comfortable / relaxed',
  'error.number': '{key} 必须是有效数字',
  'error.integer': '{key} 必须是安全整数',
  'error.range': '{key} 必须在 {min} – {max} 之间',
  'error.boolean': '{key} 必须是 boolean',
  'error.themeColor': 'themeColor 必须是 6 位十六进制颜色',
  'error.json': 'JSON 无法解析',
  'error.clipboardUnsupported': '当前环境不支持 Clipboard API',
  'error.copy': '复制失败',
  'error.export': '导出失败，请重试',
  'app.propsFailed': '当前 Props 无法渲染',
  'app.retry': '重新渲染',
  'app.exportSampled': '{format} 已导出前 2,000 行 × 128 列样本；完整极限数据建议流式后端导出',
  'app.exportDone': '{format} 导出任务已完成',
  'app.exportNotReady': '表格仍在加载，导出 API 尚未就绪，请稍后重试',
  'app.toast.close': '关闭提示',
  'app.copy.empty': '请先点击或圈选需要复制的单元格',
  'app.copy.done': '选区已复制为 TSV，可直接粘贴到 Excel',
  'app.copy.failed': '复制失败',
  'table.empty': '暂无数据',
  'table.expandRow': '展开此行',
  'table.collapseRow': '折叠此行',
  'table.nodeLoadError': '节点加载失败',
  'table.notMounted': '表格尚未挂载',
  'table.excelColumns': 'Excel 单个工作表最多支持 16,384 列，请缩小导出列范围',
  'table.excelRows': '包含表头后超过 Excel 的 1,048,576 行上限，请缩小导出行范围',
  'table.exportLimitInvalid': 'exportCellLimit 必须是正安全整数',
  'table.exportTooLarge': '导出范围包含 {count} 个单元格，超过客户端上限 {limit}；请传入更小的 range 或使用后端流式导出',
  'demo.column.region': '区域',
  'demo.column.tree': '业务组合 / 层级',
  'demo.column.product': '产品系列',
  'demo.column.revenue': '收入',
  'demo.column.orders': '订单数',
  'demo.column.completion': '目标达成',
  'demo.column.change': '同比',
  'demo.column.status': '健康度',
  'demo.column.owner': '负责人',
  'demo.column.trend': '近 7 日趋势',
  'demo.column.updated': '更新时间',
  'demo.column.metric': '指标 {index}',
  'demo.column.portfolio': '项目组合',
  'demo.column.stage': '里程碑',
  'demo.column.risk': '风险信号',
  'demo.column.budget': '预算',
  'demo.column.margin': '利润率',
  'demo.column.sla': 'SLA',
  'demo.column.quality': '质量',
  'demo.column.rules': '规则',
  'demo.plan': '{value}% 计划',
  'demo.rule.dimension': '维度',
  'demo.rule.dataBar': '数据条',
  'demo.rule.colorScale': '三色色阶',
  'demo.rule.iconSet': '图标集',
  'demo.rule.text': '文本规则',
  'demo.rule.background': '背景规则',
  'demo.rule.signedBar': '正负数据条',
  'demo.rule.thresholds': '阈值',
  'demo.rule.layeredDom': '分层 DOM',
  'demo.rule.count': '{count} 条规则',
  'demo.node.group': '分组',
  'demo.node.portfolio': '项目组合',
  'demo.node.workstream': '工作流',
  'demo.node.node': '节点',
  'demo.quality.good': '良好',
  'demo.quality.warning': '警告',
  'demo.growth': '增长',
  'demo.decline': '下降',
  'demo.avatar': '{name}头像',
  'demo.trend': '趋势值 {value}',
  'demo.status.exceed': '超预期',
  'demo.status.healthy': '健康',
  'demo.status.watch': '关注',
  'demo.status.risk': '风险',
  'demo.channel.direct': '直营',
  'demo.channel.partner': '伙伴',
  'demo.channel.ecommerce': '电商',
  'demo.channel.enterprise': '企业服务',
  'demo.region.east': '华东',
  'demo.region.south': '华南',
  'demo.region.north': '华北',
  'demo.region.southwest': '西南',
  'demo.region.northeast': '东北',
  'demo.region.overseas': '海外',
} as const

export type MessageKey = keyof typeof zh
export type MessageParams = Record<string, string | number>

const en: Record<MessageKey, string> = {
  'locale.label': 'Interface language',
  'scenario.intro': 'Overview',
  'scenario.intro.detail': 'Architecture, capabilities, and publishing boundaries',
  'scenario.gallery': 'Component gallery',
  'scenario.gallery.detail': 'Production cases and core capabilities',
  'scenario.analysis': 'Business analytics',
  'scenario.analysis.detail': 'KPIs and composite cells',
  'scenario.spreadsheet': 'Spreadsheet',
  'scenario.spreadsheet.detail': 'Excel-style editing and cell formatting',
  'analysis.eyebrow': 'BUSINESS COCKPIT · 2026 Q2',
  'analysis.title': 'Omnichannel business health',
  'analysis.live': 'Data refreshed',
  'analysis.period': 'This quarter',
  'analysis.view.flat': 'Region view',
  'analysis.view.tree': 'Portfolio view',
  'analysis.kpi.revenue': 'Revenue to date',
  'analysis.kpi.revenue.note': '$4.46M above last period',
  'analysis.kpi.attainment': 'Target attainment',
  'analysis.kpi.attainment.note': '4 of 6 regions on target',
  'analysis.kpi.margin': 'Blended margin',
  'analysis.kpi.margin.note': '58% high-margin product mix',
  'analysis.kpi.risk': 'Units at risk',
  'analysis.kpi.risk.note': '3 require action this week',
  'analysis.narrative.label': 'AI business insight',
  'analysis.narrative.text': 'East leads revenue while Nova BI lifts margin. Overseas attainment is soft; prioritize partner delivery and collections cadence.',
  'analysis.detail.title': 'Business unit detail',
  'analysis.detail.subtitle': 'Region × product × metric · drillable composite rendering',
  'analysis.legend.label': 'Cell rendering capabilities',
  'analysis.legend.component': 'Composite UI',
  'analysis.legend.dataBar': 'Data bars',
  'analysis.legend.colorScale': 'Color scales',
  'analysis.legend.rules': 'Icons / text rules',
  'analysis.section.label': 'Business analytics column group',
  'analysis.section.core': 'Core metrics',
  'analysis.section.signals': 'Risk signals',
  'analysis.cell.productMix': '{value}% revenue mix',
  'analysis.cell.orders': '{value} new',
  'spreadsheet.bookLabel': 'Sales plan.xlsx',
  'spreadsheet.toolbar.tabs': 'Spreadsheet menu',
  'spreadsheet.toolbar.label': 'Cell formatting toolbar',
  'spreadsheet.tab.home': 'Home',
  'spreadsheet.tab.formulas': 'Formulas',
  'spreadsheet.tab.view': 'View',
  'spreadsheet.saved': 'Changes applied',
  'spreadsheet.sessionReady': 'Demo data · saved in this tab',
  'spreadsheet.unsaved': 'Not saved to a file · recovered in this tab',
  'spreadsheet.group.history': 'History',
  'spreadsheet.group.clipboard': 'Clipboard',
  'spreadsheet.group.font': 'Font',
  'spreadsheet.group.alignment': 'Alignment',
  'spreadsheet.group.number': 'Number',
  'spreadsheet.group.editing': 'Editing',
  'spreadsheet.undo': 'Undo',
  'spreadsheet.redo': 'Redo',
  'spreadsheet.copy': 'Copy',
  'spreadsheet.cut': 'Cut',
  'spreadsheet.paste': 'Paste',
  'spreadsheet.font': 'Font',
  'spreadsheet.fontSize': 'Font size',
  'spreadsheet.bold': 'Bold',
  'spreadsheet.italic': 'Italic',
  'spreadsheet.underline': 'Underline',
  'spreadsheet.textColor': 'Text color',
  'spreadsheet.fillColor': 'Fill color',
  'spreadsheet.color.apply': 'Apply the last text color, {color}',
  'spreadsheet.fill.apply': 'Apply the last fill color, {color}',
  'spreadsheet.color.palette': 'Open the {target} palette',
  'spreadsheet.alignLeft': 'Align left',
  'spreadsheet.alignCenter': 'Align center',
  'spreadsheet.alignRight': 'Align right',
  'spreadsheet.wrap': 'Wrap text',
  'spreadsheet.currency': 'Currency format',
  'spreadsheet.percent': 'Percent format',
  'spreadsheet.number': 'Two decimal places',
  'spreadsheet.numberFormat': 'Number format',
  'spreadsheet.numberFormat.general': 'General',
  'spreadsheet.numberFormat.number': 'Number',
  'spreadsheet.numberFormat.currency': 'Currency',
  'spreadsheet.numberFormat.percent': 'Percentage',
  'spreadsheet.merge': 'Merge selected cells',
  'spreadsheet.unmerge': 'Unmerge cells',
  'spreadsheet.reset': 'Reset sheet',
  'spreadsheet.clearFormat': 'Clear formats',
  'spreadsheet.nameBox': 'Name box',
  'spreadsheet.nameBoxHint': 'Enter C4 or B2:D8 and press Enter to navigate',
  'spreadsheet.formulaBar': 'Formula bar',
  'spreadsheet.insertFunction': 'Insert function',
  'spreadsheet.cellEditor': 'Edit cell',
  'spreadsheet.cancelEdit': 'Cancel edit',
  'spreadsheet.acceptEdit': 'Accept edit',
  'spreadsheet.gridLabel': 'Sales plan spreadsheet',
  'spreadsheet.sheet.subtitle': 'Sales operations · base currency · refreshed 07/14 18:30',
  'spreadsheet.sheetName': 'Business tracker',
  'spreadsheet.status.summary': 'Count {count} · Average {average} · Sum {sum}',
  'spreadsheet.status.count': 'Count {count}',
  'spreadsheet.formula.quick': 'Quick functions',
  'spreadsheet.formula.sum': 'Sum',
  'spreadsheet.formula.average': 'Average',
  'spreadsheet.formula.min': 'Minimum',
  'spreadsheet.formula.max': 'Maximum',
  'spreadsheet.formula.count': 'Count',
  'spreadsheet.formula.help': 'Select data and insert a function. Review or edit the formula, then press Enter to accept or Escape to cancel.',
  'spreadsheet.view.gridlines': 'Gridlines',
  'spreadsheet.view.freeze': 'Freeze heading rows',
  'spreadsheet.view.formulaBar': 'Formula bar',
  'spreadsheet.view.zoom': 'Zoom',
  'spreadsheet.view.zoomIn': 'Zoom in',
  'spreadsheet.view.zoomOut': 'Zoom out',
  'spreadsheet.feedback.formatted': 'Formatting applied',
  'spreadsheet.feedback.formatCleared': 'Formatting cleared',
  'spreadsheet.feedback.cleared': 'Contents cleared',
  'spreadsheet.feedback.invalidAddress': 'Invalid address. Try C4 or B2:D8',
  'spreadsheet.feedback.copied': 'Selection copied',
  'spreadsheet.feedback.copiedInternal': 'Copied inside the grid; system clipboard permission was not granted',
  'spreadsheet.feedback.cut': 'Range ready to move; paste to move it or press Escape to cancel',
  'spreadsheet.feedback.cutClipboardFailed': 'Cut canceled: the system clipboard could not be written, so source data was kept',
  'spreadsheet.feedback.cutChanged': 'Cut canceled because the worksheet changed before paste',
  'spreadsheet.feedback.cutCancelled': 'Cut canceled; source data was kept',
  'spreadsheet.cutPendingStatus': 'Move pending: {range} · Escape to cancel',
  'spreadsheet.feedback.pasted': 'Pasted',
  'spreadsheet.feedback.pastedCount': 'Pasted {count} cells',
  'spreadsheet.feedback.clipboardEmpty': 'Clipboard is empty',
  'spreadsheet.feedback.pasteTooLarge': 'Paste range is too large',
  'spreadsheet.feedback.pasteOutOfBounds': 'Cannot paste {rows} × {columns} from {address}: the target exceeds the worksheet boundary',
  'spreadsheet.feedback.mergeConflict': 'The range conflicts with an existing merged cell',
  'spreadsheet.feedback.mergeRange': 'Select two or more cells first',
  'spreadsheet.feedback.merged': 'Cells merged',
  'spreadsheet.feedback.unmerged': 'Cells unmerged',
  'spreadsheet.feedback.formulaInserted': 'Function formula is ready to edit; press Enter to accept or Escape to cancel',
  'spreadsheet.feedback.reset': 'Worksheet reset',
  'spreadsheet.feedback.undo': 'Undone',
  'spreadsheet.feedback.redo': 'Redone',
  'spreadsheet.confirm.merge': 'Merging will clear {count} non-empty cells outside the top-left cell. You can undo this action. Continue?',
  'spreadsheet.confirm.reset': 'Reset will replace the current worksheet data and formatting. You can undo this action. Continue?',
  'gallery.eyebrow': 'Runnable examples',
  'gallery.title': 'Verify each public capability',
  'gallery.intro': 'Validate composed behavior in real workflows first, then inspect the rendering foundation one capability at a time. Only one grid mounts at once.',
  'gallery.group.production': 'Production cases',
  'gallery.group.basic': 'Basic capabilities',
  'gallery.group.advanced': 'Advanced capabilities',
  'gallery.editor.open': 'Edit source',
  'gallery.editor.close': 'Close source',
  'gallery.editor.title': 'Component implementation',
  'gallery.editor.live': 'Live render',
  'gallery.editor.reset': 'Reset source',
  'gallery.editor.hint': 'The preview refreshes automatically as you edit TSX',
  'gallery.editor.preview': 'Live preview',
  'intro.eyebrow': 'Studio · application layer · rendering foundation',
  'intro.title': 'Studio + application grid + rendering foundation',
  'intro.detail': 'Studio owns experience and configuration, Insight composes application-grid behavior, and Core owns high-performance visible DOM rendering. Each layer has a clear boundary and composes on demand.',
  'intro.stats.scale': 'logical cells',
  'intro.stats.examples': 'runnable examples',
  'intro.stats.packages': 'architecture layers',
  'intro.layers.title': 'Repository and publishing boundaries',
  'intro.layer.studio': 'Interactive demos, live Props, source entry points, and diagnostics.',
  'intro.layer.studio.meta': 'Studio layer',
  'intro.layer.insight': 'Trees, conditional formats, business cells, and export.',
  'intro.layer.insight.meta': 'application grid',
  'intro.layer.core': 'Virtualization, freezing, merging, selection, and keyboard interaction.',
  'intro.layer.core.meta': 'grid rendering foundation',
  'intro.capabilities.title': 'Capability coverage',
  'intro.capabilities.basic': 'Two-axis virtualization · Four-edge freezing · Custom and adaptive sizing · Click, drag, out-of-bounds scroll and Shift selection · Arrow/Tab/Enter · TSV copy · Themes and appearance · Empty states, events, meta, ARIA and i18n',
  'intro.capabilities.advanced': '100K × 100K lazy data · Explicit, adjacent-value and tree-boundary merges · Flat and arbitrary-depth async trees · Five conditional formats with rule priority · Typography, images, icons and React components · Mobile touch selection, direct column resize and copy · Imperative APIs · Excel/CSV/PNG',
  'gallery.virtualization.title': '100K × 100K virtualization',
  'gallery.virtualization.detail': 'A 10-billion-cell address space renders only the viewport and a small DOM buffer.',
  'gallery.frozen.title': 'Frozen on four edges',
  'gallery.frozen.detail': 'Top, bottom, left, and right regions share one scrolling coordinate system.',
  'gallery.sizing.title': 'Sizing and container fit',
  'gallery.sizing.detail': 'Sparse size Maps, content measurement, and spare-width stretching compose together.',
  'gallery.sizing.dimension': 'Dimension {index}',
  'gallery.sizing.measured': 'Measured from visible content',
  'gallery.sizing.cell': 'Row {row} · Column {column}',
  'gallery.merging.title': 'Adjacent dimension merging',
  'gallery.merging.detail': 'Adjacent equal dimensions merge vertically and coexist with explicit horizontal merges.',
  'gallery.merging.region': 'Region',
  'gallery.merging.product': 'Product',
  'gallery.merging.revenue': 'Revenue',
  'gallery.merging.orders': 'Orders',
  'gallery.merging.margin': 'Margin',
  'gallery.merging.summary': 'Horizontal summary',
  'gallery.merging.east': 'East',
  'gallery.merging.south': 'South',
  'gallery.merging.north': 'North',
  'gallery.merging.cloud': 'Cloud',
  'gallery.merging.data': 'Data',
  'gallery.merging.ai': 'AI',
  'gallery.selection.title': 'Selection and copy',
  'gallery.selection.detail': 'Clicks, arrow keys, drag ranges, and TSV copy share one controlled selection.',
  'gallery.selection.toolbar': 'Controlled range',
  'gallery.selection.tsv': 'Copy TSV',
  'gallery.selection.rows': '{count} rows copied',
  'gallery.selection.value': 'Value {row}.{column}',
  'gallery.renderer.title': 'Custom cell rendering',
  'gallery.renderer.detail': 'Images, typography, conditional visuals, and embedded React components keep a clear DOM hierarchy.',
  'gallery.renderer.owner': 'Owner',
  'gallery.renderer.team': 'Team',
  'gallery.renderer.score': 'Score',
  'gallery.renderer.status': 'Status',
  'gallery.renderer.team.growth': 'Growth',
  'gallery.renderer.team.platform': 'Platform',
  'gallery.renderer.team.commerce': 'Commerce',
  'gallery.renderer.team.data': 'Data',
  'gallery.renderer.team.ai': 'AI',
  'gallery.renderer.team.cloud': 'Cloud',
  'gallery.renderer.status.healthy': 'Healthy',
  'gallery.renderer.status.watch': 'Watch',
  'gallery.renderer.status.risk': 'At risk',
  'gallery.tree.title': 'Multi-level tree row model',
  'gallery.tree.detail': 'Root and intermediate branches expand independently while leaves retain stable data coordinates.',
  'gallery.tree.commercial': 'Commercial',
  'gallery.tree.direct': 'Direct',
  'gallery.tree.partners': 'Partners',
  'gallery.tree.product': 'Product',
  'gallery.tree.cloud': 'Cloud',
  'gallery.tree.data': 'Data',
  'gallery.tree.enterprise': 'Enterprise',
  'gallery.tree.midmarket': 'Mid-market',
  'gallery.tree.platform': 'Platform',
  'gallery.tree.applications': 'Applications',
  'gallery.tree.analytics': 'Analytics',
  'gallery.tree.governance': 'Governance',
  'gallery.tree.dimension': 'Dimension',
  'gallery.tree.value': 'Value',
  'gallery.conditional.title': 'Five conditional formats',
  'gallery.conditional.detail': 'Text, background, icons, color scales, and data bars compose independently in one grid.',
  'gallery.conditional.entity': 'Business unit',
  'gallery.conditional.unit': 'Unit {index}',
  'gallery.conditional.bar': 'Data bar',
  'gallery.conditional.scale': 'Color scale',
  'gallery.conditional.icon': 'Icon',
  'gallery.conditional.rule': 'Text and background',
  'gallery.lazy.title': 'Lazy row and column data',
  'gallery.lazy.detail': 'rowSource and getColumn generate 100K rows × 10K columns by coordinate without materializing full arrays.',
  'gallery.lazy.column': 'Metric {index}',
  'gallery.api.title': 'Imperative API',
  'gallery.api.detail': 'Use apiRef to locate cells, control a selection, and restore grid focus.',
  'gallery.api.toolbar': 'API controls',
  'gallery.api.jump': 'Jump to 24,000 × 240',
  'gallery.api.select': 'Select remote range',
  'gallery.orders.title': 'Order fulfillment center',
  'gallery.orders.detail': 'A 10K-order window combines frozen identifiers, SLA risk, money, dates, long text, and copy-ready selection.',
  'gallery.budget.title': 'Annual budget matrix',
  'gallery.budget.detail': 'Cost centers span budget, actual, and variance columns across the year to stress wide scrolling, frozen dimensions, and signed signals.',
  'gallery.mobile.title': 'Mobile field inspection',
  'gallery.mobile.detail': 'Field work orders combine direction-locked touch scrolling, tap selection, handle extension, direct resizing, and safe-area copy in a real mobile viewport.',
  'gallery.mobile.state.selection': 'Current range',
  'gallery.mobile.state.resize': 'Latest resize',
  'gallery.mobile.state.title': 'Live state',
  'gallery.mobile.deviceLabel': 'Mobile device preview',
  'gallery.mobile.referenceSize': 'Desktop reference viewport',
  'gallery.mobile.deviceTitle': 'Field inspection work orders',
  'gallery.mobile.deviceMeta': '{count} inspection work orders',
  'gallery.mobile.deviceLive': 'Live',
  'gallery.mobile.copy': 'Copy range',
  'gallery.mobile.copied': 'Copied',
  'gallery.mobile.copyError': 'Copy failed',
  'gallery.mobile.selectionHandle': 'Drag to extend selection',
  'gallery.mobile.selectionActions': 'Selection actions',
  'gallery.mobile.range': 'R{rowStart}–{rowEnd} · C{columnStart}–{columnEnd}',
  'gallery.mobile.rangeEmpty': 'No selection',
  'gallery.mobile.resizeHandle': 'Resize column {column}',
  'gallery.mobile.resizeReady': 'Header edges are draggable',
  'gallery.mobile.resized': 'C{column} · {width}px',
  'gallery.mobile.column.item': 'Work order',
  'gallery.mobile.column.owner': 'Engineer',
  'gallery.mobile.column.progress': 'SLA',
  'gallery.mobile.column.status': 'Status',
  'gallery.mobile.column.metric': 'Inspection {index}',
  'gallery.mobile.item': 'WO-{index}',
  'gallery.mobile.owner': 'Engineer {index}',
  'gallery.mobile.status.ready': 'Completed',
  'gallery.mobile.status.active': 'In progress',
  'gallery.mobile.status.watch': 'SLA risk',
  'table.copySelection': 'Copy',
  'table.copySuccess': 'Copied',
  'table.copyError': 'Copy failed',
  'table.selectionHandle': 'Drag to extend selection',
  'table.selectionActions': 'Selection actions',
  'table.resizeColumn': 'Resize column: {column}',
  'gallery.export.title': 'Excel, CSV, and image',
  'gallery.export.detail': 'One grid API exports a structured workbook, raw data, or the current visible viewport.',
  'gallery.export.unit': 'Business unit {index}',
  'gallery.export.excel': 'Export Excel',
  'gallery.export.csv': 'Export CSV',
  'gallery.export.png': 'Export PNG',
  'preset.everyday': 'Everyday data',
  'preset.wide': 'Ultra-wide report',
  'preset.maximum': 'Maximum scale',
  'preset.custom': 'Custom scale',
  'studio.scenarios': 'Extension scenarios',
  'studio.empty.title': 'Preview stage is ready',
  'studio.empty.body': 'Inject an UltiGrid component through renderStage; props arrive through its context.',
  'studio.sampling': 'Sampling',
  'studio.diagnostics': 'Render diagnostics',
  'studio.diagnostics.source': 'Browser animation frames and viewport callbacks',
  'studio.diagnostics.note': 'Session observations are not a cross-device benchmark.',
  'studio.diagnostics.close': 'Close render diagnostics',
  'studio.diagnostics.raf': 'rAF',
  'studio.diagnostics.p95': 'P95',
  'studio.diagnostics.cells': 'DOM',
  'studio.diagnostics.visible': 'Visible',
  'studio.diagnostics.warming': 'Warming up',
  'studio.diagnostics.idle': 'Idle',
  'studio.diagnostics.scrolling': 'Scrolling',
  'studio.diagnostics.hidden': 'Page hidden',
  'studio.reset': 'Reset configuration',
  'studio.export': 'Export',
  'studio.export.preparing': 'Table loading; export is not ready',
  'studio.exporting': 'Exporting {format}',
  'studio.export.dialog': 'Choose an export format',
  'studio.export.title': 'Export view',
  'studio.export.subtitle': 'Choose a format for your next workflow',
  'studio.export.close': 'Close export panel',
  'studio.export.sample': 'This logical grid is large. Excel and CSV will safely sample the first 2,000 rows × 128 columns.',
  'studio.export.excel': 'Excel workbook',
  'studio.export.excel.detail': '.xlsx · preserves structure and merges',
  'studio.export.image': 'Current view image',
  'studio.export.image.detail': '.png · captures the visible viewport',
  'studio.export.csv': 'Raw data',
  'studio.export.csv.detail': '.csv · raw data or a safe sample',
  'studio.inspector.toggle': 'Toggle props panel',
  'studio.inspector.collapse': 'Collapse props panel',
  'studio.inspector.expand': 'Expand props panel',
  'studio.stage.label': 'UltiGrid Insight preview stage',
  'studio.scale': 'Data scale',
  'studio.scale.preset': 'Data scale preset',
  'studio.fullscreen.enter': 'Enter fullscreen',
  'studio.fullscreen.exit': 'Exit fullscreen',
  'studio.fullscreen.failed': 'Fullscreen is unavailable in this environment',
  'studio.render.failed': 'Rendering could not complete',
  'studio.render.retry': 'Try rendering again',
  'studio.render.loading': 'Building the visible window',
  'studio.render.loading.detail': 'Only viewport DOM nodes are created',
  'studio.status.pinnedRows': '{count} pinned rows',
  'studio.status.pinnedColumns': '{count} pinned columns',
  'studio.status.cells': 'Cells {count}',
  'studio.status.scroll': '{count} px/s',
  'studio.status.live': 'Live',
  'studio.status.rendering': 'Rendering',
  'studio.status.error': 'Error',
  'studio.inspector.close': 'Close props panel',
  'studio.inspector.label': 'Live props panel',
  'studio.inspector.title': 'Props Lab',
  'studio.inspector.resize': 'Resize props panel',
  'studio.inspector.resize.hint': 'Drag or use the left and right arrow keys to resize the panel',
  'studio.connected': 'Connected',
  'studio.editor.mode': 'Editor mode',
  'studio.editor.visual': 'Visual controls',
  'studio.editor.pending': 'Unapplied changes',
  'studio.summary.grid': 'Logical grid',
  'studio.summary.scenario': 'Current scenario',
  'studio.summary.frozen': 'Frozen edges',
  'studio.section.engine': 'Data and engine',
  'studio.section.engine.detail': 'Numbers apply on blur or Enter',
  'studio.field.rows': 'Rows',
  'studio.field.columns': 'Columns',
  'studio.field.overscanRows': 'Row overscan',
  'studio.field.overscanColumns': 'Column overscan',
  'studio.section.frozen': 'Frozen edges',
  'studio.section.frozen.detail': 'Configure every edge independently',
  'studio.field.top': 'Top rows',
  'studio.field.bottom': 'Bottom rows',
  'studio.field.left': 'Left columns',
  'studio.field.right': 'Right columns',
  'studio.section.size': 'Size and density',
  'studio.section.size.detail': 'Geometry and responsive behavior',
  'studio.field.rowHeight': 'Default row height',
  'studio.field.columnWidth': 'Default column width',
  'studio.field.density': 'Interface density',
  'studio.density.compact': 'Compact',
  'studio.density.comfortable': 'Comfortable',
  'studio.density.relaxed': 'Relaxed',
  'studio.field.fitColumns': 'Fit columns',
  'studio.field.fitColumns.hint': 'Share spare container width',
  'studio.field.autoRowHeight': 'Auto row height',
  'studio.field.autoRowHeight.hint': 'Measure visible multiline content',
  'studio.section.presentation': 'Presentation',
  'studio.section.presentation.detail': 'Lightweight visual options',
  'studio.field.themeColor': 'Theme color',
  'studio.field.gridLines': 'Show grid lines',
  'studio.field.striped': 'Alternate row fill',
  'studio.field.rowNumbers': 'Show row numbers',
  'studio.field.treeEnabled': 'Tree view',
  'studio.field.mergeSameValueDimensions': 'Merge matching dimensions',
  'studio.field.treeExpanded': 'Expand tree nodes by default',
  'studio.json.title': 'Validate, then apply',
  'studio.json.body': 'Edits stay in a draft. Press ⌘↵ or Apply after validation to avoid rebuilding an extreme grid while typing.',
  'studio.json.copy': 'Copy',
  'studio.json.copied': 'Copied',
  'studio.json.copyTitle': 'Copy JSON',
  'studio.json.discard': 'Discard draft',
  'studio.json.discardTitle': 'Discard draft',
  'studio.json.validPending': 'Schema valid · waiting to apply',
  'studio.json.synced': 'Synced with preview',
  'studio.json.restore': 'Restore defaults',
  'studio.json.apply': 'Apply props',
  'studio.shortcuts': 'Shortcuts',
  'studio.shortcuts.panel': 'Panel',
  'studio.shortcuts.reset': 'Reset',
  'app.source.open': 'View source',
  'error.propsObject': 'Props must be a JSON object',
  'error.scenario': 'scenario must be intro, gallery, analysis, or spreadsheet',
  'error.density': 'density must be compact, comfortable, or relaxed',
  'error.number': '{key} must be a valid number',
  'error.integer': '{key} must be a safe integer',
  'error.range': '{key} must be between {min} and {max}',
  'error.boolean': '{key} must be boolean',
  'error.themeColor': 'themeColor must be a 6-digit hex color',
  'error.json': 'JSON could not be parsed',
  'error.clipboardUnsupported': 'Clipboard API is unavailable in this environment',
  'error.copy': 'Copy failed',
  'error.export': 'Export failed. Try again.',
  'app.propsFailed': 'The current props could not render',
  'app.retry': 'Render again',
  'app.exportSampled': '{format} exported the first 2,000 rows × 128 columns. Use a streaming backend for the full extreme dataset.',
  'app.exportDone': '{format} export completed',
  'app.exportNotReady': 'The table is still loading and its export API is not ready. Try again shortly.',
  'app.toast.close': 'Close notification',
  'app.copy.empty': 'Select one or more cells before copying',
  'app.copy.done': 'Selection copied as TSV and ready for Excel',
  'app.copy.failed': 'Copy failed',
  'table.empty': 'No data',
  'table.expandRow': 'Expand row',
  'table.collapseRow': 'Collapse row',
  'table.nodeLoadError': 'Node failed to load',
  'table.notMounted': 'The grid is not mounted',
  'table.excelColumns': 'An Excel worksheet supports at most 16,384 columns. Reduce the export range.',
  'table.excelRows': 'The export exceeds Excel’s 1,048,576-row worksheet limit after adding the header.',
  'table.exportLimitInvalid': 'exportCellLimit must be a positive safe integer',
  'table.exportTooLarge': 'The export contains {count} cells, above the client limit of {limit}. Use a smaller range or a streaming backend.',
  'demo.column.region': 'Region',
  'demo.column.tree': 'Portfolio / hierarchy',
  'demo.column.product': 'Product family',
  'demo.column.revenue': 'Revenue',
  'demo.column.orders': 'Orders',
  'demo.column.completion': 'Target progress',
  'demo.column.change': 'YoY',
  'demo.column.status': 'Health',
  'demo.column.owner': 'Owner',
  'demo.column.trend': '7-day trend',
  'demo.column.updated': 'Updated',
  'demo.column.metric': 'Metric {index}',
  'demo.column.portfolio': 'Portfolio',
  'demo.column.stage': 'Milestone',
  'demo.column.risk': 'Risk signal',
  'demo.column.budget': 'Budget',
  'demo.column.margin': 'Margin',
  'demo.column.sla': 'SLA',
  'demo.column.quality': 'Quality',
  'demo.column.rules': 'Rules',
  'demo.plan': '{value}% plan',
  'demo.rule.dimension': 'Dimension',
  'demo.rule.dataBar': 'Data bar',
  'demo.rule.colorScale': '3-color scale',
  'demo.rule.iconSet': 'Icon set',
  'demo.rule.text': 'Text rule',
  'demo.rule.background': 'Background',
  'demo.rule.signedBar': 'Signed bar',
  'demo.rule.thresholds': 'Thresholds',
  'demo.rule.layeredDom': 'Layered DOM',
  'demo.rule.count': '{count} rules',
  'demo.node.group': 'Group',
  'demo.node.portfolio': 'Portfolio',
  'demo.node.workstream': 'Workstream',
  'demo.node.node': 'Node',
  'demo.quality.good': 'Good',
  'demo.quality.warning': 'Warning',
  'demo.growth': 'Growth',
  'demo.decline': 'Decline',
  'demo.avatar': '{name} avatar',
  'demo.trend': 'Trend value {value}',
  'demo.status.exceed': 'Exceeding',
  'demo.status.healthy': 'Healthy',
  'demo.status.watch': 'Watch',
  'demo.status.risk': 'At risk',
  'demo.channel.direct': 'Direct',
  'demo.channel.partner': 'Partners',
  'demo.channel.ecommerce': 'Commerce',
  'demo.channel.enterprise': 'Enterprise',
  'demo.region.east': 'East China',
  'demo.region.south': 'South China',
  'demo.region.north': 'North China',
  'demo.region.southwest': 'Southwest',
  'demo.region.northeast': 'Northeast',
  'demo.region.overseas': 'International',
}

export type Translate = (key: MessageKey, params?: MessageParams) => string

export function translate(locale: Locale, key: MessageKey, params?: MessageParams): string {
  const dictionary = locale === 'en-US' ? en : zh
  const template = dictionary[key]
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (match, name: string) => (
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : match
  ))
}

interface I18nContextValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: Translate
}

const I18nContext = createContext<I18nContextValue>({
  locale: 'zh-CN',
  setLocale: () => undefined,
  t: (key, params) => translate('zh-CN', key, params),
})

const STORAGE_KEY = 'ultigrid.locale'

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(() => {
    if (typeof window === 'undefined') return 'zh-CN'
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return stored === 'en-US' || stored === 'zh-CN' ? stored : 'zh-CN'
  })

  useEffect(() => {
    document.documentElement.lang = locale
    window.localStorage.setItem(STORAGE_KEY, locale)
  }, [locale])

  const t = useCallback<Translate>((key, params) => translate(locale, key, params), [locale])
  const value = useMemo(() => ({ locale, setLocale, t }), [locale, t])
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nContextValue {
  return useContext(I18nContext)
}
